/**
 * War Room Page — Phase C.6
 *
 * Main deliberation view composing all War Room components:
 * - Summary header with stats
 * - Agent summary cards (one per AI agent)
 * - Tab group: Issues | Timeline
 * - Each issue card supports inline human input
 *
 * Route: /deliberation/:reportId
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  MessageSquare,
  Play,
  RotateCw,
  Wrench,
  Activity,
  Users,
  Loader2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  type CaseDeliberation,
  type DeliberationSummary,
  type DeliberationEvent,
  getDeliberation,
  getFleetDeliberation,
  getSummary,
  getTriggerStatus,
  rerunEngines,
  runFleet,
  getFleetStatus,
  AgentToolTiming,
} from '@/services/deliberationApi';
import { AgentSummaryCard } from '@/components/deliberation/AgentSummaryCard';
import { EscalationCard } from '@/components/deliberation/EscalationCard';
import { safeText } from '@/lib/safeText';
import { IssueCard } from '@/components/deliberation/IssueCard';
import { TimelineView } from '@/components/deliberation/TimelineView';
import { DeliberationErrorBoundary } from '@/components/deliberation/DeliberationErrorBoundary';
import { ChallengesTab } from '@/components/deliberation/ChallengesTab';
import ConsistencyBanner from '@/components/deliberation/ConsistencyBanner';
import { SettlementIntelligenceTab } from '@/components/deliberation/SettlementIntelligenceTab';
import { BriefTab } from '@/components/deliberation/BriefTab';
import AppLayout from '@/components/AppLayout';
import { getCaseProfile } from '@/services/caseProfileService';
import { UnconfirmedPostureBanner } from '@/components/shared/UnconfirmedPostureBanner';
import type { CaseProfilePayload } from '@/components/shared/caseProfile';

// =============================================================================
// TYPES
// =============================================================================

type TabKey = 'brief' | 'issues' | 'timeline' | 'challenges' | 'settlement';

/** Derived stats from fleet deliberation events — client-side computation
 *  eliminates the data-source mismatch between stats bar and seat cards.
 */
export interface DerivedDeliberationStats {
  totalEvents: number;
  toolsUsed: number;
  disagreements: number;
  humanInputs: number;
  reAnalyses: number;
}

interface AgentSummaryData {
  agentName: string;
  confidence: number | null;
  claimValue: string | null;
  toolsUsed: number;
  keyFinding: string;
  resolutionPathsCount: number;
  totalImpact: string | null;
  openItems: number | null;   // curable blockers suppressing a claimant-side score
  projected: string | null;   // projected score range once blockers clear, e.g. "72-75"
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Derive deliberation stats client-side from the fleet deliberation object.
 *
 * This eliminates the data-source mismatch where the stats bar was calling
 * GET /deliberation/{id}/summary while seat cards used the fleet deliberation.
 *
 * Falls back to summary values only when deliberation.events is absent or empty.
 */
export function deriveDeliberationStats(
  deliberation: CaseDeliberation,
  summary: DeliberationSummary | null,
): DerivedDeliberationStats {
  const events = deliberation.events ?? [];

  // If we have events, compute client-side; otherwise fall back to summary
  const hasEvents = events.length > 0;

  const totalEvents = hasEvents
    ? events.length
    : (summary?.total_events ?? 0);

  const toolsUsed = hasEvents
    ? events.filter((e) => e.event_type === 'tool_call').length
    : (summary?.tools_used ?? 0);

  // Count open issues with agent_positions array length > 1 (true disagreements)
  const issues = deliberation.issues ?? [];
  const disagreements = hasEvents
    ? issues.filter(
        (issue) =>
          issue.status === 'open' &&
          Object.keys(issue.agent_positions).length > 1
      ).length
    : (summary?.disagreements_found ?? 0);

  const humanInputs = hasEvents
    ? events.filter((e) => e.event_type === 'human_input').length
    : (summary?.human_inputs ?? 0);

  const reAnalyses = hasEvents
    ? events.filter((e) => e.event_type === 're_analysis').length
    : (summary?.re_analyses_triggered ?? 0);

  return {
    totalEvents,
    toolsUsed,
    disagreements,
    humanInputs,
    reAnalyses,
  };
}
export function buildAgentSummaries(deliberation: CaseDeliberation): AgentSummaryData[] {
  const agentMap = new Map<string, {
    events: DeliberationEvent[];
    toolNames: Set<string>;
    resolutionPaths: number;
    totalImpact: number;
  }>();

  // Pre-seed all three canonical agents so every War Room run renders
  // all three cards — even when an agent crashed or produced zero
  // events. A missing card on the UI used to mean "agent failed silently
  // and you have no idea why." After this seed, a 0-event agent renders
  // with "No findings yet" and 0 tools used, which is itself the
  // diagnostic signal that the agent didn't run / crashed early.
  const CANONICAL_AI_AGENTS = ['ai:forensic', 'ai:attorney', 'ai:adjuster', 'ai:defendant_attorney'];
  const withheldSeats = deliberation.withheld_seats ?? [];
  for (const agentName of CANONICAL_AI_AGENTS) {
    if (withheldSeats.includes(agentName)) continue;
    agentMap.set(agentName, {
      events: [],
      toolNames: new Set(),
      resolutionPaths: 0,
      totalImpact: 0,
    });
  }

  // Only consider AI agents for summary cards
  for (const event of deliberation.events) {
    if (!event.agent_name.startsWith('ai:')) continue;

    if (!agentMap.has(event.agent_name)) {
      agentMap.set(event.agent_name, {
        events: [],
        toolNames: new Set(),
        resolutionPaths: 0,
        totalImpact: 0,
      });
    }
    const entry = agentMap.get(event.agent_name)!;
    entry.events.push(event);
    if (event.tool_name) {
      entry.toolNames.add(event.tool_name);
    }
  }

  // Count resolution paths per agent from issues
  for (const issue of deliberation.issues) {
    for (const rp of issue.resolution_paths) {
      if (rp.source_agent && agentMap.has(rp.source_agent)) {
        const entry = agentMap.get(rp.source_agent)!;
        entry.resolutionPaths++;
        if (rp.impact_value) {
          entry.totalImpact += parseFloat(rp.impact_value) || 0;
        }
      }
    }
  }

  const summaries: AgentSummaryData[] = [];

  for (const [agentName, data] of Array.from(agentMap.entries())) {
    // Find most recent position event for confidence and value
    const positionEvents = data.events
      .filter((e) => e.event_type === 'position')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    const latestPosition = positionEvents[0] || null;

    // Find key finding — agent_response > finding > position. agent_response
    // events carry the agent's final synthesized text (the actual analysis
    // output); finding events are diagnostic markers; position events carry
    // numeric anchors. Card body should prefer the richest available source.
    const responseEvents = data.events
      .filter((e) => e.event_type === 'agent_response')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    const findingEvents = data.events
      .filter((e) => e.event_type === 'finding')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Extract a card-sized headline from the agent's latest response.
    // Agent responses are serialized as JSON objects with an "executive_brief"
    // key. tool_result_summary is sometimes truncated in the DB before the
    // JSON closes, so we try full parse first then fall back to regex extraction
    // that tolerates an open-ended string value.
    const decodeUnicode = (s: string) =>
      s.replace(/\\u([\da-f]{4})/gi, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );

    const extractHeadline = (text: unknown): string | null => {
      // Backend has stored non-strings (e.g. {}) in tool_result_summary —
      // rendering those crashes React (error #31); parse strings only.
      if (typeof text !== 'string' || !text) return null;

      // 1. Full JSON parse (works when summary is not truncated)
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object') {
          const brief =
            parsed.executive_brief ||
            parsed.finding ||
            parsed.summary ||
            parsed.text;
          if (brief && typeof brief === 'string' && brief.trim().length > 20) {
            return brief.length > 280 ? brief.slice(0, 277) + '…' : brief.trim();
          }
        }
      } catch {
        // Truncated or invalid JSON — continue to regex fallbacks
      }

      // 2. Regex: complete "executive_brief": "…value…" (closed quote)
      const fullMatch = text.match(/"executive_brief"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (fullMatch) {
        const brief = decodeUnicode(fullMatch[1]).trim();
        if (brief.length > 20) return brief.length > 280 ? brief.slice(0, 277) + '…' : brief;
      }

      // 3. Regex: truncated value — grab everything after the opening quote
      const partialMatch = text.match(/"executive_brief"\s*:\s*"([^"]{20,})/);
      if (partialMatch) {
        const brief = decodeUnicode(partialMatch[1]).trim();
        return brief.length > 280 ? brief.slice(0, 277) + '…' : brief;
      }

      // 4. Plain-text fallback: strip code fences and leading JSON blobs
      const cleaned = text
        .replace(/```[\w]*\n?/g, '')   // strip code fences
        .replace(/^\s*\{[\s\S]*$/, '')  // strip JSON-only responses (no narrative prefix)
        .trim();
      const firstLine = cleaned.split(/\n+/).find((l) => l.trim().length > 20);
      if (!firstLine) return null;
      // Cap to ~280 chars so the card stays compact
      return firstLine.length > 280 ? firstLine.slice(0, 277) + '…' : firstLine;
    };

    const keyFinding =
      extractHeadline(responseEvents[0]?.tool_result_summary) ||
      safeText(findingEvents[0]?.tool_result_summary) ||
      safeText(latestPosition?.tool_result_summary) ||
      'No findings yet';

    summaries.push({
      agentName,
      confidence: latestPosition?.confidence ?? null,
      claimValue: latestPosition?.value ?? null,
      toolsUsed: data.toolNames.size,
      keyFinding,
      resolutionPathsCount: data.resolutionPaths,
      totalImpact: data.totalImpact > 0 ? String(data.totalImpact) : null,
      openItems: (latestPosition as any)?.open_items ?? null,
      projected: (latestPosition as any)?.projected ?? null,
    });
  }

  // Sort: the four deliberating seats first, then the diagnostic pipeline seats
  const order: Record<string, number> = {
    'ai:forensic': 0,
    'ai:attorney': 1,
    'ai:adjuster': 2,
    'ai:defendant_attorney': 3,
    'ai:document_auditor': 4,
    'ai:data_specialist': 5,
  };
  summaries.sort((a, b) => (order[a.agentName] ?? 99) - (order[b.agentName] ?? 99));

  return summaries;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function WarRoom() {
  const { reportId } = useParams<{ reportId: string }>();

  const [deliberation, setDeliberation] = useState<CaseDeliberation | null>(null);
  const [summary, setSummary] = useState<DeliberationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('brief');
  const [analysisState, setAnalysisState] = useState<'idle' | 'triggering' | 'running' | 'complete' | 'failed'>('idle');
  const [analysisMessage, setAnalysisMessage] = useState<string>('');
  // F1: run-level MCP-degradation reason (null = healthy). A run whose seats intended
  // engine tools but silently ran the no-tools legacy path is surfaced here.
  const [mcpDegraded, setMcpDegraded] = useState<{ reason: string | null } | null>(null);
  const [challengesRefreshTrigger, setChallengesRefreshTrigger] = useState(0);
  const [agentTiming, setAgentTiming] = useState<Record<string, AgentToolTiming> | null>(null);
  const [caseProfile, setCaseProfile] = useState<CaseProfilePayload | null>(null);
  // F7: bumped on run completion + manual refresh so the escalation card runs a
  // grace re-poll for a just-created decision-required item (the reducer that
  // opens it runs AFTER the fleet finalizes `completed`).
  const [escalationRefresh, setEscalationRefresh] = useState(0);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    getCaseProfile(reportId).then((cp) => {
      if (!cancelled) setCaseProfile(cp);
    });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const fetchData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      // Prefer the completed agent-fleet run (four seats, incl. the adversarial
      // defendant-attorney). Fall back to the classic 3-seat case_deliberations doc
      // when no completed fleet run exists (404 → getFleetDeliberation throws).
      const [delibRaw, summ] = await Promise.all([
        getFleetDeliberation(reportId).catch(() => getDeliberation(reportId)),
        getSummary(reportId),
      ]);
      // Defensive normalize: the UI reads deliberation.issues and .events directly, so
      // guarantee both are arrays regardless of source (the fleet doc omits some fields
      // the classic doc carries). Missing either field would crash the render (black page).
      const delib = {
        ...delibRaw,
        issues: (delibRaw as any).issues ?? [],
        events: (delibRaw as any).events ?? [],
      } as CaseDeliberation;
      setDeliberation(delib);
      setSummary(summ);
      setAnalysisState('idle');
      // Op-FU9 step 4: fetch run status for tool timing (non-blocking; best-effort)
      getTriggerStatus(reportId)
        .then(s => { if (s.agent_timing) setAgentTiming(s.agent_timing); })
        .catch(() => {/* timing is observability-only; silence failures */});
      // F1: reflect the last fleet run's MCP-degradation state on load (best-effort).
      getFleetStatus(reportId)
        .then(fs => setMcpDegraded(fs.mcp_degraded ? { reason: fs.mcp_degraded_reason || null } : null))
        .catch(() => {/* best-effort; absence of a run is not an error here */});
    } catch (err: any) {
      setError(err.message || 'Failed to load deliberation data');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  // Poll FLEET run status until complete or failed. The Re-run button launches
  // the 6-seat fleet (the pipeline this page actually renders), so completion
  // must be read from fleet_runs — the legacy war_room trigger status never
  // reflects fleet progress (the pre-2026-07-16 wiring polled it and the page
  // appeared to "never update" after a re-run).
  const startPolling = useCallback(() => {
    if (!reportId || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getFleetStatus(reportId);
        if (status.status === 'completed') {
          setAnalysisState('complete');
          setAnalysisMessage('Fleet analysis complete — results updated');
          setMcpDegraded(status.mcp_degraded ? { reason: status.mcp_degraded_reason || null } : null);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          fetchData();
          setEscalationRefresh((n) => n + 1);  // F7: grace re-poll for a just-created escalation
        } else if (status.status === 'failed' || status.status === 'orphaned') {
          setAnalysisState('failed');
          setAnalysisMessage(status.error || `Fleet analysis ${status.status}`);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch {
        // Polling error — keep trying
      }
    }, 15000);
  }, [reportId, fetchData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  const handleTriggerAnalysis = useCallback(async () => {
    if (!reportId) return;
    setAnalysisState('triggering');
    setAnalysisMessage('Refreshing calculation engines…');
    try {
      // Engines FIRST: the fleet's agents read engine_outputs, and skipping
      // this silently carries pre-upload figures into the new run (backend
      // fleet.py docstring mandates this ordering; engine refresh completes
      // in seconds and blocks until done).
      await rerunEngines(reportId);
      const res = await runFleet(reportId);
      setAnalysisState('running');
      setAnalysisMessage(`Fleet analysis running (${res.run_id}) — typically 25–40 minutes…`);
      startPolling();
    } catch (err: any) {
      const msg = String(err?.message || '');
      setAnalysisState('failed');
      setAnalysisMessage(
        msg.includes('409') || msg.toLowerCase().includes('in flight')
          ? 'A fleet run is already in flight for this report — wait for it to finish.'
          : msg || 'Failed to start fleet analysis',
      );
    }
  }, [reportId, startPolling]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build agent summaries from deliberation data
  const agentSummaries = useMemo(() => {
    if (!deliberation) return [];
    return buildAgentSummaries(deliberation);
  }, [deliberation]);

  // Derive stats client-side from fleet deliberation (Fix A: eliminates data-source mismatch)
  const derivedStats = useMemo(() => {
    if (!deliberation) return null;
    return deriveDeliberationStats(deliberation, summary);
  }, [deliberation, summary]);

  // Sort events by timestamp for timeline
  const sortedEvents = useMemo(() => {
    if (!deliberation) return [];
    return [...deliberation.events].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [deliberation]);

  // =========================================================================
  // LOADING STATE
  // =========================================================================

  if (loading && !deliberation) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 mx-auto mb-4" style={{ borderBottom: '2px solid var(--color-brand)' }} />
            <p className="cs-meta">Loading War Room...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // =========================================================================
  // ERROR STATE
  // =========================================================================

  if (error && !deliberation) {
    // Check if this is a 404 (no analysis yet) vs a real error
    const isNoData = error.includes('not found') || error.includes('404') || error.includes('No deliberation');

    if (isNoData) {
      return (
        <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
          <div className="rounded-xl shadow-lg p-8 max-w-lg text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            {analysisState === 'running' || analysisState === 'triggering' ? (
              <>
                <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--color-brand)' }} />
                <h2 className="cs-h2 mb-2">
                  Agents Are Deliberating...
                </h2>
                <p className="cs-body mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Four AI agents (Forensic, Attorney, Adjuster, and Defense Counsel) are analyzing your case with toolkit-enabled reasoning.
                </p>
                <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>
                  {analysisMessage || 'This typically takes 30-90 seconds. The page will auto-refresh when complete.'}
                </p>
              </>
            ) : analysisState === 'failed' ? (
              <>
                <span className="inline-flex mx-auto mb-4 justify-center w-full" style={{ color: 'var(--color-needs-human-text)' }}><XCircle className="h-12 w-12" /></span>
                <h2 className="cs-h2 mb-2">Analysis Failed</h2>
                <p className="cs-body mb-4" style={{ color: 'var(--color-text-secondary)' }}>{analysisMessage}</p>
                <button
                  onClick={handleTriggerAnalysis}
                  className="cs-label px-6 py-3 rounded-lg transition-colors"
                  style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  Try Again
                </button>
              </>
            ) : analysisState === 'complete' ? (
              <>
                <span className="inline-flex mx-auto mb-4 justify-center w-full" style={{ color: 'var(--color-verified)' }}><CheckCircle2 className="h-12 w-12" /></span>
                <h2 className="cs-h2 mb-2">Analysis Complete</h2>
                <p className="cs-body mb-4" style={{ color: 'var(--color-text-secondary)' }}>{analysisMessage}</p>
                <button
                  onClick={fetchData}
                  className="cs-label px-6 py-3 rounded-lg transition-colors"
                  style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  View Results
                </button>
              </>
            ) : (
              <>
                <span className="inline-flex mx-auto mb-4 justify-center w-full" style={{ color: 'var(--color-brand)' }}><Users className="h-12 w-12" /></span>
                <h2 className="cs-h2 mb-2">
                  No Analysis Yet
                </h2>
                <p className="cs-body mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                  Start the War Room to have four AI agents (Forensic Analyst, Attorney Advisor, Insurance Adjuster, and adversarial Defense Counsel) independently analyze this case with tool-calling capabilities, then compare their findings.
                </p>
                <button
                  onClick={handleTriggerAnalysis}
                  className="cs-label inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors shadow-sm"
                  style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                >
                  <Play className="h-4 w-4" />
                  Start War Room Analysis
                </button>
              </>
            )}
          </div>
        </div>
        </AppLayout>
      );
    }

    // Real error (not 404)
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
          <div className="rounded-xl shadow-lg p-8 max-w-md text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <span className="inline-flex mx-auto mb-4 justify-center w-full" style={{ color: 'var(--color-needs-human-text)' }}><AlertTriangle className="h-12 w-12" /></span>
            <h2 className="cs-h2 mb-2">Failed to Load</h2>
            <p className="cs-body mb-4" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
            <button
              onClick={fetchData}
              className="cs-label px-4 py-2 rounded-lg transition-colors"
              style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              Retry
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!deliberation || !summary) return null;

  // =========================================================================
  // TABS CONFIG
  // =========================================================================

  const tabs: { key: TabKey; label: string; count: number | null }[] = [
    { key: 'brief', label: 'Brief', count: null },
    { key: 'issues', label: 'Disputed Issues', count: deliberation.issues.length },
    { key: 'challenges', label: 'Challenges', count: null },
    { key: 'settlement', label: 'Settlement', count: null },
    { key: 'timeline', label: 'Timeline', count: deliberation.events.length },
  ];

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <AppLayout>
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Running banner */}
      {(analysisState === 'running' || analysisState === 'triggering') && (
        <div className="cs-body px-6 py-2 flex items-center gap-3" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>Agents are deliberating — this takes 30–90 seconds. Results will appear automatically when complete.</span>
        </div>
      )}

      {/* F1: run-level MCP-degradation warning — a run whose agents lost engine tools.
          Uses the dedicated --color-warning verification-state token (kept separate from
          the accent so it can diverge later, even though it currently maps to the same hue). */}
      {mcpDegraded && (
        <div
          className="cs-body px-6 py-2 flex items-center gap-2"
          style={{
            background: 'var(--color-bg-surface)',
            borderBottom: '1px solid var(--color-warning)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: 'var(--color-warning)' }} />
          <span>
            This run ran without engine tools (MCP degraded) — figures may lack engine
            grounding.{mcpDegraded.reason ? ` (${mcpDegraded.reason})` : ''}
          </span>
        </div>
      )}

      <UnconfirmedPostureBanner profile={caseProfile} className="mx-6 mt-4" />

      {/* Header */}
      <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/report/${reportId}`}
                className="transition-colors"
                style={{ color: 'var(--color-text-dim)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="cs-h2">Agent War Room</h1>
                <p className="cs-meta">
                  Case {reportId} · Deliberation Analysis
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleTriggerAnalysis}
                disabled={analysisState === 'running' || analysisState === 'triggering'}
                className="cs-label flex items-center gap-2 px-3 py-2 rounded-lg disabled:opacity-50 transition-colors"
                style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
              >
                <Play className={`h-4 w-4 ${analysisState === 'running' || analysisState === 'triggering' ? 'animate-pulse' : ''}`} />
                {analysisState === 'running' || analysisState === 'triggering' ? 'Running…' : 'Re-run Analysis'}
              </button>
              <button
                onClick={() => { fetchData(); setChallengesRefreshTrigger(t => t + 1); setEscalationRefresh(n => n + 1); }}
                disabled={loading}
                className="cs-label flex items-center gap-2 px-3 py-2 rounded-lg disabled:opacity-50 transition-colors"
                style={{ color: 'var(--color-text-secondary)', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
              >
                <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* WS-3 consistency gate — factual contradictions vs the parsed record.
            Renders nothing until the gate has annotated the deliberation. */}
        <ConsistencyBanner
          report={deliberation.consistency_report}
          status={deliberation.consistency_status}
        />

        {/* F7 (12-factor #7): tracked human-escalation card — decisions the agents
            reached an impasse on and need a human to resolve. Renders nothing when
            there are no decision-required items. */}
        <DeliberationErrorBoundary sectionName="Decisions Required">
          <EscalationCard reportId={reportId!} refreshSignal={escalationRefresh} />
        </DeliberationErrorBoundary>

        {/* Summary Stats Bar — Fix A: derived client-side from fleet deliberation */}
        {derivedStats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard
              icon={<Activity className="h-4 w-4" style={{ color: 'var(--color-brand)' }} />}
              label="Total Events"
              value={String(derivedStats.totalEvents)}
            />
            <StatCard
              icon={<Wrench className="h-4 w-4" style={{ color: 'var(--color-brand)' }} />}
              label="Tools Used"
              value={String(derivedStats.toolsUsed)}
            />
            <StatCard
              icon={<AlertTriangle className="h-4 w-4" style={{ color: 'var(--color-brand)' }} />}
              label="Disagreements"
              value={String(derivedStats.disagreements)}
            />
            <StatCard
              icon={<MessageSquare className="h-4 w-4" style={{ color: 'var(--color-verified)' }} />}
              label="Human Inputs"
              value={String(derivedStats.humanInputs)}
            />
            <StatCard
              icon={<RotateCw className="h-4 w-4" style={{ color: 'var(--color-brand)' }} />}
              label="Re-Analyses"
              value={String(derivedStats.reAnalyses)}
            />
          </div>
        )}

        {/* Agent Summary Cards */}
        {agentSummaries.length > 0 && (
          <DeliberationErrorBoundary sectionName="Agent Summaries">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {agentSummaries.map((agent) => (
                <AgentSummaryCard
                  key={agent.agentName}
                  agentName={agent.agentName}
                  confidence={agent.confidence}
                  claimValue={agent.claimValue}
                  toolsUsed={agent.toolsUsed}
                  keyFinding={agent.keyFinding}
                  resolutionPathsCount={agent.resolutionPathsCount}
                  totalImpact={agent.totalImpact}
                  openItems={agent.openItems}
                  projected={agent.projected}
                />
              ))}
            </div>
          </DeliberationErrorBoundary>
        )}

        {/* Tool Timing Panel — Op-FU9 step 4 (observability only, collapses when no data) */}
        {agentTiming && Object.keys(agentTiming).length > 0 && (() => {
          const timingEntries = Object.entries(agentTiming) as [string, AgentToolTiming][];
          const totalCalls = timingEntries.reduce((s, [, t]) => s + t.tool_count, 0);
          const totalMs   = timingEntries.reduce((s, [, t]) => s + t.total_ms, 0);
          return (
            <details className="rounded-xl shadow-sm" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <summary className="cs-label flex items-center gap-2 px-4 py-3 cursor-pointer select-none" style={{ color: 'var(--color-text-secondary)' }}>
                <Wrench className="h-4 w-4" style={{ color: 'var(--color-brand)' }} />
                Tool Timing
                <span className="cs-meta ml-auto">
                  {totalCalls} calls · {Math.round(totalMs / 1000)}s total
                </span>
              </summary>
              <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {timingEntries.map(([agent, timing]) => (
                  <div key={agent} className="space-y-1">
                    <p className="cs-eyebrow" style={{ color: 'var(--color-text-secondary)' }}>
                      {agent.charAt(0).toUpperCase() + agent.slice(1)}
                    </p>
                    <p className="cs-meta">
                      {timing.tool_count} calls · {Math.round(timing.total_ms)}ms
                      {timing.error_count > 0 && (
                        <span className="ml-1" style={{ color: 'var(--color-needs-human-text)' }}>· {timing.error_count} error{timing.error_count > 1 ? 's' : ''}</span>
                      )}
                    </p>
                    <div className="space-y-0.5">
                      {Object.entries(timing.per_tool)
                        .sort(([, a], [, b]) => b.total_ms - a.total_ms)
                        .slice(0, 5)
                        .map(([tool, stats]) => (
                          <div key={tool} className="flex items-center justify-between gap-2">
                            <span className="cs-meta truncate font-mono" title={tool}>
                              {tool.length > 24 ? tool.slice(0, 22) + '…' : tool}
                            </span>
                            <span className="cs-meta shrink-0" style={{ color: 'var(--color-text-dim)' }}>
                              {stats.count}× · {Math.round(stats.total_ms)}ms
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          );
        })()}

        {/* Tab Selector */}
        <div className="flex items-center gap-1 rounded-lg p-1 w-fit" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="cs-label flex items-center gap-2 px-4 py-2 rounded-md transition-colors"
              style={
                activeTab === tab.key
                  ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                  : { color: 'var(--color-text-secondary)', background: 'transparent' }
              }
              onMouseEnter={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.background = 'var(--color-bg-hover)'; e.currentTarget.style.color = 'var(--color-text-primary)'; } }}
              onMouseLeave={(e) => { if (activeTab !== tab.key) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)'; } }}
            >
              {tab.label}
              {tab.count !== null && (
                <span
                  className="cs-chip px-1.5 py-0.5 rounded-full"
                  style={
                    activeTab === tab.key
                      ? { background: 'rgba(0,0,0,0.2)', color: 'var(--color-on-brand)' }
                      : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                  }
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'brief' && (
          <DeliberationErrorBoundary sectionName="Brief">
            <BriefTab reportId={reportId!} />
          </DeliberationErrorBoundary>
        )}

        {activeTab === 'issues' && (
          <DeliberationErrorBoundary sectionName="Disputed Issues">
            <div className="space-y-4">
              {(() => {
                // Filter out the phantom "uncategorized" issue that older Mongo
                // documents may contain. This bucket was created when untagged
                // events (agent_response synth, submit_analysis output) were
                // incorrectly clustered into issues. PR #447 fixed the backend
                // aggregator, but stale deliberations persisted before that fix
                // may still carry this phantom issue.
                //
                // Rule: hide "uncategorized" if it has no agent positions AND
                // no resolution paths — it's structural telemetry, not a real
                // disputed issue.
                const actionableIssues = deliberation.issues.filter((issue) => {
                  if (issue.tag !== 'uncategorized') return true;
                  const hasPositions = Object.keys(issue.agent_positions).length > 0;
                  const hasPaths = issue.resolution_paths.length > 0;
                  return hasPositions || hasPaths;
                });

                if (actionableIssues.length === 0) {
                  return (
                    <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
                      <Users className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--color-text-ghost)' }} />
                      <p className="cs-body">No disputed issues found in this deliberation.</p>
                    </div>
                  );
                }

                return actionableIssues.map((issue) => (
                  <IssueCard
                    key={issue.tag}
                    issue={issue}
                    reportId={reportId!}
                    onInputSubmitted={fetchData}
                  />
                ));
              })()}
            </div>
          </DeliberationErrorBoundary>
        )}

        {activeTab === 'challenges' && (
          <DeliberationErrorBoundary sectionName="Challenges">
            <ChallengesTab reportId={reportId!} refreshTrigger={challengesRefreshTrigger} />
          </DeliberationErrorBoundary>
        )}

        {activeTab === 'settlement' && (
          <DeliberationErrorBoundary sectionName="Settlement Intelligence">
            <SettlementIntelligenceTab reportId={reportId!} />
          </DeliberationErrorBoundary>
        )}

        {activeTab === 'timeline' && (
          <DeliberationErrorBoundary sectionName="Timeline">
            <TimelineView events={sortedEvents} />
          </DeliberationErrorBoundary>
        )}
      </div>
    </div>
    </AppLayout>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg px-4 py-3" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="cs-meta">{label}</span>
      </div>
      <p className="cs-figure" style={{ color: 'var(--color-text-primary)' }}>{value}</p>
    </div>
  );
}
